// src/components/UpdatePassword.js

import React, { useState, useEffect } from 'react';
import { FaKey } from 'react-icons/fa';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import supabase from '../supabase';

const UpdatePassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract the access_token from the URL query parameters
  const query = new URLSearchParams(location.search);
  const accessToken = query.get('access_token');

  useEffect(() => {
    if (!accessToken) {
      setMessage('Invalid or missing token.');
      console.error('Access token missing in URL.');
    } else {
      console.log('Access token found:', accessToken);
    }
  }, [accessToken]);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.updateUser(
        { password },
        { access_token: accessToken }
      );

      if (error) {
        console.error('Error updating password:', error.message);
        setMessage(`Error: ${error.message}`);
      } else {
        console.log('Password updated successfully:', data);
        setMessage('Password updated successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setMessage('An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-orange-500 via-red-500 to-black">
      <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="flex justify-center mb-2">
          <FaKey className="text-blue-800 text-3xl"/>
        </div>

        <h1 className="mt-4 text-2xl font-bold text-center text-blue-800"> 
          Set New Password
        </h1>
        
        <p className="mt-4 text-sm text-center text-blue-500">
          Enter your new password below
        </p>

        {/* Password Reset Form */}
        <form onSubmit={handlePasswordUpdate} className="mt-6">
          {/* New Password Input */}
          <div>
            <label htmlFor="new-password" className="sr-only">New Password</label>
            <input
              id="new-password"
              type="password"
              placeholder="New Password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 mt-2 text-sm text-blue-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>

          {/* Confirm Password Input */}
          <div className="mt-4">
            <label htmlFor="confirm-password" className="sr-only">Confirm New Password</label>
            <input
              id="confirm-password"
              type="password"
              placeholder="Confirm New Password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 mt-2 text-sm text-blue-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>

          {/* Update Password Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-2 mt-4 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-yellow-400"
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        {/* Back to Login Link */}
        <p className="mt-4 text-sm text-center text-blue-600">
          Remembered your password?{' '}
          <Link to="/" className="text-yellow-500 hover:underline">
            Login
          </Link>
        </p>

        {/* Message */}
        {message && <p className="mt-4 text-sm text-center text-red-500">{message}</p>}
      </div>
    </div>
  );
};

export default UpdatePassword;
